import {useRef, Fragment, useState, useEffect, useMemo, Suspense} from 'react';
import {TransformControls} from '@react-three/drei'
import {mergeBufferGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import {extend, useFrame, useThree} from '@react-three/fiber';
import {Line as Line2} from '@react-three/drei'

import {useAnimStore} from './index';

// import {DeformingElement} from './deformingelement';
import {DeformingElementTraj} from './deformingelementtraj';
import {CustomElement} from './customelement';
// import {CustomElementTraj} from './customelementtraj';

import {isZero, isNonzero} from './computation';
import {calculateRotQuat} from './visualmath';

import {count,zeros,norm} from 'mathjs';
import {Vector3, Color, DoubleSide} from 'three';

extend({TransformControls})

export const ElementsTraj = (props) => {

    const getGeom = (e,i) => {
        let points1 = new Vector3();
        let points2 = new Vector3();
        let points = [];
        
        // find index of nodes given nodeid
        const ii_p = props.nodes.findIndex( no => no.id === e.connect[0]);
        const ii_q = props.nodes.findIndex( no => no.id === e.connect[1]);

        points1.fromArray(props.nodes[ii_p].coord);
        points2.fromArray(props.nodes[ii_q].coord);
        points.push(points1);
        points.push(points2);

        let style = [];

        if (e.type === 'leaf'){
          style.w = 100;
          style.t = 10;
          style.color = "steelblue";
        }
        else if(e.type === 'rigid') {
          style.w = 120;
          style.t = 120;
          style.color = "slategray";
        }
        else if(e.type === 'wire') {
          style.w = 10;
          style.t = 10;
          style.color = "green";
        }
        else if(e.type === 'notch') {
          style.w = 100;
          style.t = 10;
          style.color = "orange";
        }

        const elementReturn = DeformingElementTraj({
            id: e.id,
            rp0: points1,
            rq0: points2,
            coord: points,
            locY: e.locY,
            type: e.type,
            rp: e.traj.rp,
            rq: e.traj.rq,
            qp: e.traj.qp,
            qq: e.traj.qq,
            q0: e.q0,
            w: e.dim.w,
            t: e.dim.t,
            set: e.set,
            style: style,
            visSettings: props.visSettings
          })

        return elementReturn;

    }

    //separate elements structure per (explode) set
    const elementSet1 = props.elements.filter( e => e.set === 1 && e.type === 'leaf');
    const elementSet2 = props.elements.filter( e => e.set === 2 && e.type === 'leaf');

    //apply the getGeom function to each set, to get the deformed geometries
    let geom = [];  
    geom[0] = elementSet1.map(getGeom);
    geom[1] = elementSet2.map(getGeom);

    //compute vertex count per geometry
    const vertexCount = 8*props.visSettings.lineSteps*props.visSettings.crossSteps + 4*props.visSettings.wSteps*props.visSettings.tSteps;

    const nSets = geom.length; //number of explode sets
    const nLoadsteps = geom[0][0].length; //number of loadsteps

    //reorganize structure, so that loadsteps come before elements in array, so that loadsteps can be set with setdrawrange
    let geomNew = []; //this array will contain all geometries, and in right order
    let nElements = []; //number of elements per set may change
    for(let k=0; k<nSets; k++){
      nElements[k] = geom[k].length; 
      let allGeom = [];
      for(let i=0; i<nLoadsteps; i++){
        // loop over loadsteps
        for(let j=0; j<nElements[k]; j++){
          // loop over elements
          allGeom.push(geom[k][j][i]);
        }
      }
      geomNew[k] = mergeBufferGeometries(allGeom);
    }

    //get geometries of stl parts
    const customSet1 = props.elements.filter( e => e.set === 1 && e.type === 'custom');
    const customSet2 = props.elements.filter( e => e.set === 2 && e.type === 'custom');
    const customSet0 = props.elements.filter( e => e.set === undefined && e.type === 'custom');

    const customGeom1 = customSet1.map(CustomElement);
    const customGeom2 = customSet2.map(CustomElement);
    const customGeom0 = customSet0.map(CustomElement);

    const rate = props.visSettings.rate;
    const steps = props.elements[0].traj.rp.length

    //---set up animation of loadsteps---
    let time = 0;
    let idx = 0;
    let getIndex = (delta) => {
     time += delta;
     return Math.round( (Math.sin(time * rate) +1)/2*(steps-1) );
    };

    //get show state, store in reference toggleRef, use in useFrame
    const toggleRef = useRef(useAnimStore.getState().show);
    useEffect( () => useAnimStore.subscribe(
     show => (toggleRef.current = show),
     state => state.show
     ), [])

    const {invalidate} = useThree();

    let iiPrev = 0;
    let iiNow = 0;
    useFrame( (state,delta)=>{
     invalidate();
     if(toggleRef.current){
       iiNow = getIndex(delta);
       if (iiNow !== iiPrev) {
         doLoadstep(iiNow);
         iiPrev = iiNow;
         useAnimStore.setState({lstep: iiNow});
       }
     }
    });
    //---end of setting up animation of load steps---

    //set initial loadstep, to be shown upon loading page
    useEffect(()=>{
     doLoadstep(0);
    })

    console.log(steps)

    const doLoadstep = (lstep) => {
      geomNew[0].setDrawRange(3*vertexCount*nElements[0]*lstep, 3*vertexCount*nElements[0]);
      geomNew[1].setDrawRange(3*vertexCount*nElements[1]*lstep, 3*vertexCount*nElements[1]);
    }

    const doExplode = (explode) => {
      set1.current.position.z = explode;
      set2.current.position.z = -explode;
    }

    //execute doLoadstep upon state change of lstep variable
    useEffect( () => useAnimStore.subscribe(
      lstep => doLoadstep(lstep),
      state => state.lstep
    ), [])

    //execute doExplode upon state change of explode variable
    useEffect( () => useAnimStore.subscribe(
     explode => doExplode(explode),
     state => state.explode
     ), [])

    const set1 = useRef();
    const set2 = useRef();

    return (
     <Fragment>
       <mesh ref={set1}>
         <mesh geometry={geomNew[0]}>
           <meshPhongMaterial color={"steelblue"} side={DoubleSide}/>
         </mesh>
         {customGeom1}
       </mesh>
       {customGeom0}
       <mesh ref={set2}>
         <mesh geometry={geomNew[1]}>
           <meshPhongMaterial color={"steelblue"} side={DoubleSide}/>
         </mesh>
         {customGeom2}
       </mesh>
     </Fragment>
    );
};